import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useQueueMonitor } from '../../hooks/useQueueMonitor';
import { formatTime, getTimeElapsed } from '../../utils/helpers';
import WaiverModal from './WaiverModal';
import './QueueMonitor.css';

const QueueMonitor = () => {
  const { terminalInfo, logout } = useAuth();
  const { queue, loading, error, lastUpdate, refresh } = useQueueMonitor(5000);
  const [selectedWaiver, setSelectedWaiver] = useState(null);

  const handleRowClick = (item) => {
    setSelectedWaiver(item);
  };

  const handleCloseModal = () => {
    setSelectedWaiver(null);
    refresh(); // Refresh queue after closing modal
  };

  return (
    <div className="queue-monitor">
      {/* Header */}
      <header className="queue-header">
        <div className="header-left">
          <h1>Waiver Queue</h1>
          <div className="terminal-info">
            <span className="tenant-name">{terminalInfo?.tenant_name}</span>
            <span className="terminal-name">{terminalInfo?.terminal_name}</span>
            <span className="terminal-location">{terminalInfo?.location_name}</span>
          </div>
        </div>
        <div className="header-right">
          <span className="header-queue-count">{queue.length} Pending</span>
          {lastUpdate && (
            <span className="last-update">
              Updated: {formatTime(lastUpdate)}
            </span>
          )}
          <button className="refresh-btn" onClick={refresh} disabled={loading} title="Refresh">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </button>
        </div>
      </header>

      {/* Queue Content */}
      <main className="queue-content">
        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {loading && queue.length === 0 ? (
          <div className="queue-loading">
            <div className="spinner-large"></div>
            <p>Loading queue...</p>
          </div>
        ) : queue.length === 0 ? (
          <div className="queue-empty">
            <div className="empty-icon">📋</div>
            <h2>No Waivers in Queue</h2>
            <p>New waivers will appear here automatically</p>
          </div>
        ) : (
          <>
            <div className="queue-list">
              {queue.map((item) => {
                const isInProgress = item.status === 'in_progress';
                return (
                <div
                  key={item.queue_id}
                  className={`queue-item${isInProgress ? ' queue-item-disabled' : ''}`}
                  onClick={() => !isInProgress && handleRowClick(item)}
                >
                  <div className="queue-item-header">
                    <div className="queue-item-header-left">
                      <span className="customer-name">{item.guest_name}</span>
                      {item.party_size > 1 && (
                        <span className={`party-size-chip${isInProgress ? ' party-size-chip-in-progress' : ''}`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                            <circle cx="9" cy="7" r="4" />
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                          </svg>
                          Party of {item.party_size}
                        </span>
                      )}
                    </div>
                    <span className="queue-time">{getTimeElapsed(item.created_at)}</span>
                  </div>
                  <div className="queue-item-body">
                    <div className="queue-detail">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{item.guest_email || 'N/A'}</span>
                    </div>
                    <div className="queue-detail">
                      <span className="detail-label">Phone:</span>
                      <span className="detail-value">{item.guest_phone || 'N/A'}</span>
                    </div>
                    {item.waivers && item.waivers > 1 && (
                      <div className="queue-detail">
                        <span className="detail-label">Waivers:</span>
                        <span className="detail-value">{item.waivers}</span>
                      </div>
                    )}
                  </div>
                  <div className="queue-item-footer">
                    {isInProgress && (
                      <span className="status-badge status-in_progress">In Progress<span className="ellipsis-anim"></span></span>
                    )}
                    {!isInProgress && (
                      <span className="click-hint">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                        </svg>
                        Tap to Sign
                      </span>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {/* Waiver Modal */}
      {selectedWaiver && (
        <WaiverModal
          waiver={selectedWaiver}
          onClose={handleCloseModal}
        />
      )}

      {/* Footer */}
      <footer className="queue-footer">
        <button className="footer-logout-btn" onClick={logout}>
          Sign Out
        </button>
      </footer>
    </div>
  );
};

export default QueueMonitor;
